import React, { useEffect, useImperativeHandle, useRef } from 'react';
// CodeMirror core is imported by ./factory. These side-effect imports register
// the modes/addons/CSS the factory needs — placed here so they land in this
// lazily-loaded chunk (non-code pages never download CodeMirror).
import 'codemirror/mode/python/python';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/addon/edit/matchbrackets';
import 'codemirror/addon/selection/active-line';
import 'codemirror/addon/display/placeholder';
import 'codemirror/lib/codemirror.css';
import './wb-codemirror.css';
import { WBCmMount, WBCodeEditor, WBHandle } from './factory';
import type { CodeEditorProps, CodeEditorHandle } from './types';

type ImplProps = CodeEditorProps & { innerRef?: React.Ref<CodeEditorHandle> };

export default function CodeMirrorImpl(props: ImplProps) {
  const {
    value = '', language = 'python', readOnly = false, filename,
    onChange, lineWrapping, placeholder, innerRef,
  } = props;
  const chrome = props.chrome ?? !readOnly;

  const mountRef = useRef<HTMLDivElement | null>(null);
  const apiRef = useRef<WBHandle | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Instantiate once on mount; tear down the CodeMirror DOM on unmount.
  useEffect(() => {
    const hostEl = mountRef.current;
    if (!hostEl) return;
    const opts = { value, language, readOnly, filename, lineWrapping, placeholder };
    const api = chrome ? WBCodeEditor(hostEl, opts) : WBCmMount(hostEl, opts);
    apiRef.current = api;
    const cm = api.cm;
    const onCmChange = () => { const cb = onChangeRef.current; if (cb) cb(cm.getValue()); };
    if (!readOnly) cm.on('change', onCmChange);
    return () => {
      if (!readOnly) cm.off('change', onCmChange);
      apiRef.current = null;
      hostEl.innerHTML = '';     // drop CM + chrome DOM
      hostEl.className = 'wb-cm'; // reset the classes the factory toggled
    };
    // mount-once: `value`/`onChange` are handled via the ref + sync effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the editor in sync when the controlled `value` changes from OUTSIDE
  // (e.g. a programmatic reset). Skips when equal so typing never resets.
  useEffect(() => {
    const api = apiRef.current;
    if (api && value !== api.getValue()) api.setValue(value);
  }, [value]);

  useImperativeHandle(innerRef, (): CodeEditorHandle => ({
    getValue: () => (apiRef.current ? apiRef.current.getValue() : value),
    setValue: (v: string) => { if (apiRef.current) apiRef.current.setValue(v); },
    toFile: () => (apiRef.current
      ? apiRef.current.toFile()
      : new File([value], filename || 'estimator.py', { type: language === 'json' ? 'application/json' : 'text/x-python' })),
    focus: () => { if (apiRef.current) apiRef.current.focus(); },
  }), [value, filename, language]);

  return <div ref={mountRef} className="wb-cm" />;
}
